import { useState } from "react";
import { AXIS_TEXT, GRID, SURFACE, Tooltip, useWidth, type ChartSeries, type Tip } from "./chartKit";

interface Props {
  categories: string[];
  /** `values` are percentages (null = no value that week: the line breaks there); `details` is the "n of m" text for the tooltip. */
  series: (ChartSeries & { values: (number | null)[]; details?: (string | undefined)[] })[];
  ariaLabel: string;
  height?: number;
}

const M = { top: 12, right: 14, bottom: 26, left: 38 };
const TICKS = [0, 25, 50, 75, 100];

/** Percentage lines on one 0-100 axis: 2px strokes, 8px end-dots with a surface ring, a crosshair that snaps to the nearest week. */
export default function LineChart({ categories, series, ariaLabel, height = 220 }: Props) {
  const [ref, width] = useWidth();
  const [active, setActive] = useState<number | null>(null);
  const inner = { w: Math.max(40, width - M.left - M.right), h: height - M.top - M.bottom };
  const n = categories.length;
  const x = (i: number) => M.left + (n === 1 ? inner.w / 2 : (inner.w * i) / (n - 1));
  const y = (v: number) => M.top + inner.h - (v / 100) * inner.h;
  const every = Math.ceil(n / Math.max(1, Math.floor(inner.w / 64)));

  const segments = (values: (number | null)[]) => {
    const out: string[] = [];
    let cur = "";
    values.forEach((v, i) => {
      if (v === null) { if (cur) out.push(cur); cur = ""; return; }
      cur += `${cur ? "L" : "M"}${x(i)},${y(v)}`;
    });
    if (cur) out.push(cur);
    return out;
  };
  const tip: Tip | null = active === null ? null : {
    x: x(active), title: categories[active],
    rows: series.map((s) => {
      const v = s.values[active];
      return { color: s.color, label: s.label + (s.details?.[active] ? ` (${s.details[active]})` : ""), value: v === null ? "n/a" : `${v}%` };
    }),
  };
  const pick = (clientX: number, rect: DOMRect) => {
    const rel = clientX - rect.left - M.left;
    setActive(Math.max(0, Math.min(n - 1, n === 1 ? 0 : Math.round((rel / inner.w) * (n - 1)))));
  };

  return (
    <div ref={ref} className="relative">
      <svg width={width} height={height} role="img" aria-label={ariaLabel} onPointerLeave={() => setActive(null)}
        onPointerMove={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}>
        {TICKS.map((t) => (
          <g key={t}>
            <line x1={M.left} x2={width - M.right} y1={y(t)} y2={y(t)} stroke={GRID} strokeWidth={1} />
            <text x={M.left - 6} y={y(t)} textAnchor="end" dominantBaseline="middle" fontSize={11} fill={AXIS_TEXT}>{t}%</text>
          </g>
        ))}
        {categories.map((c, i) => i % every === 0 && <text key={c + i} x={x(i)} y={height - 8} textAnchor="middle" fontSize={11} fill={AXIS_TEXT}>{c}</text>)}
        {active !== null && <line x1={x(active)} x2={x(active)} y1={M.top} y2={M.top + inner.h} stroke={AXIS_TEXT} strokeWidth={1} opacity={0.5} />}
        {series.map((s) => (
          <g key={s.key}>
            {segments(s.values).map((d, i) => <path key={i} d={d} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />)}
            {s.values.map((v, i) => v !== null && (active === i || i === lastIndex(s.values)) && (
              <circle key={i} cx={x(i)} cy={y(v)} r={4} fill={s.color} stroke={SURFACE} strokeWidth={2} />
            ))}
          </g>
        ))}
        {/* keyboard: one focusable band per week, same readout as hover */}
        {categories.map((c, i) => (
          <rect key={c + i} x={x(i) - inner.w / Math.max(1, n - 1) / 2} y={M.top} width={Math.max(24, inner.w / Math.max(1, n - 1))} height={inner.h} fill="transparent"
            tabIndex={0} aria-label={`${c}: ${series.map((s) => `${s.label} ${s.values[i] === null ? "n/a" : `${s.values[i]}%`}`).join(", ")}`}
            onFocus={() => setActive(i)} onBlur={() => setActive(null)} />
        ))}
      </svg>
      <Tooltip tip={tip} width={width} />
    </div>
  );
}

function lastIndex(values: (number | null)[]): number {
  for (let i = values.length - 1; i >= 0; i--) if (values[i] !== null) return i;
  return -1;
}
